import { Trans, useLingui } from '@lingui/react/macro'
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Group,
  Paper,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'

import { formatDate, formatEuros } from '@markku/utils'
import {
  IconAlertTriangle,
  IconBarcode,
  IconCreditCard,
  IconDownload,
} from '@tabler/icons-react'
import dayjs from 'dayjs'
import { invoiceHooks } from '../../../lib/trpcHooks'
import { WireTransferModal } from './WireTransferModal'

interface Invoice {
  id: number
  createdAt: string
  invoiceNumber: string
  referenceNumber: string
  totalAmount: string
  status: 'finalized' | 'sent' | 'paid' | 'void'
  sentAt: string | null
  paidAt: string | null
  dueDate: string
  voidedInvoice?: { invoiceNumber: string } | null
}

interface InvoiceItemProps {
  invoice: Invoice
  customerType?: string
}

export function InvoiceItem({ invoice, customerType }: InvoiceItemProps) {
  const { t } = useLingui()
  const [wireTransferOpened, wireTransferHandlers] = useDisclosure(false)
  const isVoided = invoice.status === 'void'
  const isPaid = invoice.status === 'paid'
  const isOpen = !isPaid && !isVoided

  const {
    data: invoicePdf,
    isLoading: isPdfLoading,
    error: pdfError,
  } = invoiceHooks.useGetInvoicePdf(invoice.id)

  const { mutate: getPaymentUrl, isPending: isPaymentPending } =
    invoiceHooks.useGetInvoicePaymentUrl()

  const handleDownloadPdf = () => {
    if (invoicePdf?.downloadUrl) {
      window.open(invoicePdf.downloadUrl, '_blank')
    }
  }

  const handlePayNow = () => {
    getPaymentUrl({ invoiceId: invoice.id })
  }

  // Invoice date: use sentAt if available, otherwise createdAt
  const invoiceDate = formatDate(invoice.sentAt || invoice.createdAt)

  const dueDateObj = dayjs(invoice.dueDate)

  // Check if invoice is overdue (only for open invoices)
  const isOverdue = isOpen && dayjs().isAfter(dueDateObj)

  const statusBadge = isVoided ? (
    <Badge color="red" size="sm">
      <Trans>Voided</Trans>
    </Badge>
  ) : isPaid ? (
    <Badge color="green" size="sm">
      <Trans>Paid</Trans>
    </Badge>
  ) : isOverdue ? (
    <Badge color="red" size="sm">
      <Trans>Overdue</Trans>
    </Badge>
  ) : (
    <Badge color="blue" size="sm">
      <Trans>Open</Trans>
    </Badge>
  )

  // Due date: only for open invoices (not paid, not voided)
  const dueDate = isOpen ? formatDate(dueDateObj.toISOString()) : null

  return (
    <Paper p="sm" bg="gray.0" radius="sm">
      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <Group gap="xs">
            <Text
              size="sm"
              fw={500}
              td={isVoided ? 'line-through' : undefined}
              c={isVoided ? 'dimmed' : undefined}
            >
              {invoice.invoiceNumber}
            </Text>
            <Text size="sm" c="dimmed">
              {invoiceDate}
            </Text>
            {statusBadge}
          </Group>

          <Group gap="xs">
            <Text size="sm" c={isVoided ? 'dimmed' : undefined}>
              {formatEuros(invoice.totalAmount)}
            </Text>
            {dueDate && (
              <>
                <Text size="sm" c="dimmed">
                  •
                </Text>
                <Text size="sm" c="dimmed">
                  <Trans>Due</Trans> {dueDate}
                </Text>
              </>
            )}
          </Group>

          {invoice.voidedInvoice && (
            <Text size="xs" c="dimmed" fs="italic">
              <Trans>
                Replaces invoice {invoice.voidedInvoice.invoiceNumber}
              </Trans>
            </Text>
          )}

          {/* Payment buttons for open invoices */}
          {isOpen && (
            <Group gap="xs" mt="xs">
              {customerType !== 'company' && (
                <Button
                  variant="filled"
                  size="xs"
                  leftSection={<IconCreditCard size={14} />}
                  onClick={handlePayNow}
                  loading={isPaymentPending}
                >
                  <Trans>Pay now</Trans>
                </Button>
              )}
              <Button
                variant="light"
                size="xs"
                leftSection={<IconBarcode size={14} />}
                onClick={wireTransferHandlers.open}
              >
                <Trans>Wire transfer</Trans>
              </Button>
            </Group>
          )}
        </Stack>

        {(invoicePdf?.downloadUrl || isPdfLoading) && (
          <Tooltip label={t`Download invoice (PDF)`}>
            <ActionIcon
              variant="light"
              color="blue"
              loading={isPdfLoading}
              disabled={!invoicePdf?.downloadUrl}
              onClick={handleDownloadPdf}
            >
              <IconDownload size={16} />
            </ActionIcon>
          </Tooltip>
        )}
      </Group>

      {pdfError && (
        <Alert color="red" icon={<IconAlertTriangle size={16} />} mt="xs">
          <Text size="sm">
            <Trans>PDF invoice download failed. Please try again later.</Trans>
          </Text>
        </Alert>
      )}

      <WireTransferModal
        opened={wireTransferOpened}
        onClose={wireTransferHandlers.close}
        invoice={invoice}
      />
    </Paper>
  )
}
